/**
 * flow-paths.ts — the shared decision-branch model, used by BOTH flow models
 * (fb-model for messaging/event flows, jf-model for journey flows) and by the
 * one layout engine they share.
 *
 * Why this exists (2026-08-31, stage 1 of the editor-unification plan):
 * a decision used to be `{ condition, yes: Step[], no: Step[] }` in both
 * models — two literal field names threaded through the tree walkers, the
 * insert descriptor, the validator and the layout engine. A third branch could
 * not be added without touching every one of those in two places.
 *
 * A decision now carries an ORDERED LIST of named paths. Two is the default and
 * renders exactly as YES / NO did; a third is added explicitly. The list is what
 * makes N-way cheap: every helper loops instead of naming fields.
 *
 * Generic over the step type so the two models keep their own step unions —
 * unifying the DECISION shape does not mean unifying what a step can be.
 */

export interface FlowPath<S> {
  /** Stable key. `yes` / `no` are the migrated names of the original two. */
  key: string;
  /** What the operator called this branch. Empty falls back to the key's default. */
  label: string;
  steps: S[];
}

/** Three is the ceiling for now (operator ruling): met / not met / timeout is
 *  the case this exists for, and an unbounded fan-out is a different feature. */
export const FP_MAX_PATHS = 3;

/** Reserved keys, in the order a decision grows. */
export const FP_KEYS = ["yes", "no", "third"];

export const FP_DEFAULT_LABELS: Record<string, string> = {
  yes: "Yes",
  no: "No",
  third: "Otherwise",
};

/** Per-position colour. Positional rather than keyed, so a renamed path keeps
 *  the colour its column has always had. */
export const FP_STYLES: { color: string; tint: string }[] = [
  { color: "#1F8A5B", tint: "#E4F5EF" },
  { color: "#C0392B", tint: "#FCE9EC" },
  { color: "#8A5A08", tint: "#FFF4E0" },
];
export const fpStyle = (i: number) => FP_STYLES[i] ?? FP_STYLES[FP_STYLES.length - 1]!;

/** The colour a branch's connector takes. `null` index = the trunk. */
export const fpLinkColor = (i: number | null | undefined): string =>
  i == null || i < 0 ? "#CDD3DD" : fpStyle(i).color;

export const fpLabel = <S,>(p: FlowPath<S>): string =>
  (p.label && p.label.trim()) || FP_DEFAULT_LABELS[p.key] || p.key;

/** The sentinel the label picker uses to reveal a free-text field. Path labels
 *  come from a per-type vocabulary like every other choice on a decision (the
 *  condition, the end outcome, the wait timing) — with an escape hatch, because
 *  a named path is the operator's own wording far more often than a condition
 *  is. A label outside the vocabulary IS the custom case, so nothing extra is
 *  stored to remember which mode the picker was in. */
export const FP_CUSTOM = "Custom\u2026";

/** Labels offered for any decision, whatever the journey type. A type's own
 *  vocabulary prepends its specific ones. */
export const FP_COMMON_LABELS = ["Yes", "No", "Otherwise", "No answer in time"];

/** A new decision's paths: the two that have always existed. */
export function fpDefaultPaths<S>(): FlowPath<S>[] {
  return [
    { key: "yes", label: FP_DEFAULT_LABELS.yes!, steps: [] },
    { key: "no", label: FP_DEFAULT_LABELS.no!, steps: [] },
  ];
}

/** Key for the next path to add — the first reserved key not in use, then
 *  `p4`, `p5`… so a future ceiling lift needs no change here. */
export function fpNextKey<S>(paths: FlowPath<S>[]): string {
  const used = paths.map((p) => p.key);
  const free = FP_KEYS.filter((k) => used.indexOf(k) === -1)[0];
  if (free) return free;
  let n = paths.length + 1;
  while (used.indexOf(`p${n}`) > -1) n += 1;
  return `p${n}`;
}

export function fpAddPath<S>(paths: FlowPath<S>[], label?: string): FlowPath<S>[] {
  if (paths.length >= FP_MAX_PATHS) return paths;
  const key = fpNextKey(paths);
  return paths.concat([{ key, label: label ?? FP_DEFAULT_LABELS[key] ?? key, steps: [] }]);
}

/** Removing a path never leaves fewer than two — a decision with one branch is
 *  not a decision, and the layout engine has no meaning for it. */
export function fpRemovePath<S>(paths: FlowPath<S>[], key: string): FlowPath<S>[] {
  if (paths.length <= 2) return paths;
  return paths.filter((p) => p.key !== key);
}

/** Map every path's step list through a function. */
export function fpMapSteps<S>(paths: FlowPath<S>[], fn: (steps: S[], path: FlowPath<S>) => S[]): FlowPath<S>[] {
  return paths.map((p) => ({ ...p, steps: fn(p.steps, p) }));
}

/**
 * Read a decision's paths out of ANYTHING that has ever been stored:
 * the current `paths` list, the old `{ yes, no }` pair, or neither. Defensive
 * because the input is JSON off the wire, and it is the whole of the read-time
 * migration — new writes always carry `paths`, so a document upgrades itself the
 * first time it is saved. Idempotent.
 */
export function fpMigrate<S>(raw: unknown): FlowPath<S>[] {
  const src = (raw ?? {}) as Record<string, unknown>;
  const list = src.paths;
  if (Array.isArray(list) && list.length) {
    const out = list
      .filter((p) => p && typeof p === "object")
      .map((p, i) => {
        const o = p as Record<string, unknown>;
        const key = typeof o.key === "string" && o.key ? o.key : (FP_KEYS[i] ?? `p${i + 1}`);
        return {
          key,
          label: typeof o.label === "string" ? o.label : (FP_DEFAULT_LABELS[key] ?? key),
          steps: Array.isArray(o.steps) ? (o.steps as S[]) : [],
        };
      })
      .slice(0, FP_MAX_PATHS);
    /* A stored list of one is corrupt, not a design — fall through to the pair. */
    if (out.length >= 2) return out;
  }
  const yes = Array.isArray(src.yes) ? (src.yes as S[]) : [];
  const no = Array.isArray(src.no) ? (src.no as S[]) : [];
  return [
    { key: "yes", label: FP_DEFAULT_LABELS.yes!, steps: yes },
    { key: "no", label: FP_DEFAULT_LABELS.no!, steps: no },
  ];
}
